import React, { useEffect, useState } from 'react';
import DashboardLayout from './DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { businessApi, API_BASE_URL } from '../services/api';
import { getBusinessPhotoUrl, getFallbackImage } from '../utils/imageUtils';
import { FaCloudUploadAlt, FaImages, FaTrash, FaCrown, FaSpinner } from 'react-icons/fa';
import './DashboardGallery.css';

const DashboardGallery = () => {
  const { isAuthenticated, user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [userBusinesses, setUserBusinesses] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);

  // Fetch only user's businesses
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const fetchBusinesses = async () => {
      const allBusinesses = await businessApi.fetchAll();
      // normalize response to array
      const businessesArray = Array.isArray(allBusinesses)
        ? allBusinesses
        : (allBusinesses?.data || allBusinesses || []);

      const getBizId = (b) => b?.id || b?.listing_id || b?.business_id || b?.businessId || b?.listingId || b?.ID || b?.businessID;
      const getBizUserId = (b) => b?.userId || b?.userid || b?.ownerId || b?.ownerid || b?.user_id || b?.userId;
      const getBizName = (b) => b?.name || b?.business_name || b?.businessName || b?.title || b?.businessTitle;

      const myBusinesses = (businessesArray || []).filter(
        b => String(getBizUserId(b)) === String(user.id)
      ).map(b => ({
        ...b,
        _normalizedId: getBizId(b),
        _normalizedName: getBizName(b) || 'Unnamed Business'
      }));

      setUserBusinesses(myBusinesses);
      if (myBusinesses.length > 0 && !selectedBusiness) {
        setSelectedBusiness(myBusinesses[0]._normalizedId);
      }
    };
    fetchBusinesses();
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (selectedBusiness) {
      setLoading(true);
      fetch(`${API_BASE_URL}/getPhotos.php?listing_id=${selectedBusiness}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && Array.isArray(data.photos)) {
            setImages(data.photos);
          } else {
            setImages([]);
          }
          setLoading(false);
        })
        .catch(() => {
          setImages([]);
          setLoading(false);
        });
    }
  }, [selectedBusiness]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleFileUpload = async (files) => {
    if (!selectedBusiness) {
      setUploadStatus({ type: 'error', message: 'Please select a business first.' });
      return;
    }

    const formData = new FormData();
    for (let file of files) {
      if (file.type.startsWith('image/')) {
        formData.append('images[]', file);
      }
    }
    formData.append('listing_id', selectedBusiness);

    setUploading(true);
    setUploadStatus(null);

    try {
      const response = await fetch(`${API_BASE_URL}/multipaluploadPhoto.php`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();

      if (data.success) {
        setUploadStatus({ type: 'success', message: 'Images uploaded successfully!' });
        // Refresh images
        const refreshResponse = await fetch(`${API_BASE_URL}/getPhotos.php?listing_id=${selectedBusiness}`);
        const refreshData = await refreshResponse.json();
        setImages(refreshData.success && Array.isArray(refreshData.photos) ? refreshData.photos : []);
      } else {
        setUploadStatus({ type: 'error', message: data.message || 'Upload failed!' });
      }
    } catch (error) {
      setUploadStatus({ type: 'error', message: 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this image?');
    if (!confirmDelete) return;

    try {
      const response = await fetch(`${API_BASE_URL}/delete_gallery_image.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ image_id: imageId })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh images after successful deletion
        const refreshResponse = await fetch(`${API_BASE_URL}/getPhotos.php?listing_id=${selectedBusiness}`);
        const refreshData = await refreshResponse.json();
        setImages(refreshData.success && Array.isArray(refreshData.photos) ? refreshData.photos : []);
      } else {
        alert(data.message || 'Failed to delete image.');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete image.');
    }
  };

  const handleSetCover = async (imageId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/set_cover_image.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_id: imageId, business_id: selectedBusiness })
      });

      await response.json();

      // Refresh images to update cover status
      const refreshResponse = await fetch(`${API_BASE_URL}/getPhotos.php?listing_id=${selectedBusiness}`);
      const refreshData = await refreshResponse.json();
      setImages(refreshData.success && Array.isArray(refreshData.photos) ? refreshData.photos : []);
    } catch (error) {
      console.error('Error setting cover image:', error);
    }
  };

  if (!isAuthenticated) {
    return (
      <DashboardLayout>
        <div className="dashboard-gallery-container">
          <div className="gallery-header">
            <h1 className="gallery-title">Gallery / Photos</h1>
            <p className="gallery-subtitle">Please login to manage your business gallery.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (userBusinesses.length === 0) {
    return (
      <DashboardLayout>
        <div className="dashboard-gallery-container">
          <div className="gallery-header">
            <h1 className="gallery-title">Gallery / Photos</h1>
            <p className="gallery-subtitle">You don't own any businesses yet. Add a business to start managing your gallery.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="dashboard-gallery-container">
        <div className="gallery-header">
          <h1 className="gallery-title">Gallery / Photos</h1>
          <p className="gallery-subtitle">Manage and showcase your business images</p>
        </div>

        {/* Business Selector */}
        <div className="business-selector">
          <label className="selector-label" htmlFor="business-select">
            Select Business
          </label>
          <select
            id="business-select"
            className="business-select"
            value={selectedBusiness || ''}
            onChange={e => setSelectedBusiness(e.target.value)}
          >
            {userBusinesses.map((biz, idx) => (
              <option key={biz._normalizedId || idx} value={biz._normalizedId || ''}>
                {biz._normalizedName || biz.name || `Business ${idx + 1}`}
              </option>
            ))}
          </select>
        </div>

        {/* Upload Section */}
        <div className="upload-section">
          <div
            className={`upload-area ${dragOver ? 'dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FaCloudUploadAlt className="upload-icon" />
            <div className="upload-text">
              {uploading ? 'Uploading images...' : 'Drag & drop images here or click to browse'}
            </div>
            <div className="upload-subtext">
              Supports JPG, PNG, GIF up to 10MB each
            </div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="upload-input"
              disabled={uploading}
            />
          </div>

          {uploadStatus && (
            <div className={`upload-status ${uploadStatus.type === 'success' ? 'upload-success' : 'upload-error'}`}>
              {uploadStatus.message}
            </div>
          )}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="loading-container">
            <FaSpinner className="loading-spinner" />
            <div className="loading-text">Loading gallery...</div>
          </div>
        ) : images.length === 0 ? (
          <div className="empty-gallery">
            <FaImages className="empty-icon" />
            <h3 className="empty-title">No images yet</h3>
            <p className="empty-text">Start building your gallery by uploading some images above.</p>
          </div>
        ) : (
          <div className="gallery-grid">
            {images.map((img) => (
              <div key={img.id} className="gallery-item">
                <div className="gallery-image-container">
                  <img
                    src={img.src || getFallbackImage()}
                    alt={img.alt || 'Business image'}
                    className="gallery-image"
                    onError={(e) => {
                      e.target.src = getFallbackImage();
                    }}
                  />
                  {img.is_cover && (
                    <div className="cover-badge">
                      <FaCrown style={{ marginRight: '0.25rem' }} />
                      Cover
                    </div>
                  )}
                  <div className="gallery-actions">
                    {!img.is_cover && (
                      <button
                        className="action-btn set-cover-btn"
                        onClick={() => handleSetCover(img.id)}
                        title="Set as cover image"
                      >
                        <FaCrown />
                      </button>
                    )}
                    <button
                      className="action-btn delete-btn"
                      onClick={() => handleDelete(img.id)}
                      title="Delete image"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
                <div className="gallery-info">
                  <div className="gallery-caption">
                    {img.alt || 'Business Image'}
                  </div>
                  <div className="gallery-meta">
                    Uploaded recently
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardGallery;